<template>
	<div class="ReportStats u-stack-inside-sm">
		<CloHeading level="2" class="is-visuallyHidden">{{ $t('tripReport.stats') }}</CloHeading>
		<!-- People filter -->
		<ReportFilter
			v-if="confirmedPeople.length > 1"
			:tripReportId="tripReportId"
			:confirmedPeople="confirmedPeople"
			:selectedPersonId="parseInt(selectedPersonId)"
			:currentView="currentView"
		/>
		<!-- Main stats -->
		<ul class="StatsTabs" :style="{ '--statsTabColumns': numStats }">
			<li>
				<CloStat
					id="stat-species"
					tab
					icon="species"
					:stat="numSpeciesStat"
					:label="$t('data.speciesObserved')"
					:secondary="numAddtlTaxaStat > 0 ? $t('data.plusNumOtherTaxa', [numAddtlTaxaStat]) : ''"
					@click.native.prevent="$emit('view-selected', 'species')"
					:current="currentView === 'species'"
					href="?view=species"
				/>
			</li>
			<li>
				<CloStat
					id="stat-checklists"
					tab
					icon="checklist"
					:stat="numChecklistsStat"
					:label="$t('generic.Checklists')"
					@click.native.prevent="$emit('view-selected', 'checklists')"
					:current="currentView === 'checklists'"
					href="?view=checklists"
				/>
			</li>
			<li v-if="numMediaPhotos > 0">
				<CloStat
					id="stat-photos"
					tab
					icon="photo"
					:stat="numMediaPhotos"
					:label="$t('data.speciesWithPhotos')"
					@click.native.prevent="$emit('view-selected', 'photos')"
					:current="currentView === 'photos'"
					href="?view=photos"
				/>
			</li>
			<li v-if="numMediaAudios > 0">
				<CloStat
					id="stat-audio"
					tab
					icon="audio"
					:stat="numMediaAudios"
					:label="$t('data.speciesWithAudio')"
					@click.native.prevent="$emit('view-selected', 'audio')"
					:current="currentView === 'audio'"
					href="?view=audio"
				/>
			</li>
		</ul>
		<!-- Lifer stats (only visible for single-person views when lifers are present) -->
		<div v-if="showLifers" class="Report-lifers">
			<CloHeading level="3" size="5" class="Report-lifers-heading">{{ $t('data.lifers') }}</CloHeading>
			<ul class="u-unlist">
				<!-- Species lifers -->
				<li v-if="lifers.L > 0">
					<button
						class="Report-lifers-item"
						:title="$t('data.speciesLifers')"
						v-clo-tooltip
						:aria-pressed="liferFilters.includes('s')"
						@click.prevent="$emit('lifer-toggle', 's')"
					>
						<CloIcon symbol="badgeSeen" size="sm" />
						<span>{{ lifers.L }}</span>
					</button>
				</li>
				<!-- Photo lifers -->
				<li v-if="lifers.P > 0">
					<button
						class="Report-lifers-item"
						:title="$t('data.photoLifers')"
						v-clo-tooltip
						:aria-pressed="liferFilters.includes('p')"
						@click.prevent="$emit('lifer-toggle', 'p')"
					>
						<CloIcon symbol="badgePhotographed" size="sm" />
						<span>{{ lifers.P }}</span>
					</button>
				</li>
				<!-- Audio lifers -->
				<li v-if="lifers.A > 0">
					<button
						class="Report-lifers-item"
						:title="$t('data.audioLifers')"
						v-clo-tooltip
						:aria-pressed="liferFilters.includes('a')"
						@click.prevent="$emit('lifer-toggle', 'a')"
					>
						<CloIcon symbol="badgeRecordedAudio" size="sm" />
						<span>{{ lifers.A }}</span>
					</button>
				</li>
			</ul>
			<!-- Lifer info -->
			<button class="Button Button--link" @click="showDialog">
				<CloIcon symbol="help" size="sm"/>
			</button>
			<!-- Lifer dialog -->
			<CloDialog v-show="dialogOpen" id="lifer-dialog" class="Report-lifers-dialog" @close="closeDialog">
				<template #header>
					<CloHeading level="4" size="2" class="u-stack-sm">{{ $t('data.lifers') }}</CloHeading>
				</template>
				<template #body>
					<ul class="u-unlist">
						<li>
							<CloIcon symbol="badgeSeen" size="sm" />
							<span><strong>{{ $t('data.speciesLifer') }}</strong>: {{ $t('data.speciesLiferDesc') }}</span>
						</li>
						<li>
							<CloIcon symbol="badgePhotographed" size="sm" />
							<span><strong>{{ $t('data.photoLifer') }}</strong>: {{ $t('data.photoLiferDesc') }}</span>
						</li>
						<li>
							<CloIcon symbol="badgeRecordedAudio" size="sm" />
							<span><strong>{{ $t('data.audioLifer') }}</strong>: {{ $t('data.audioLiferDesc') }}</span>
						</li>
					</ul>
				</template>
			</CloDialog>
		</div>
	</div>
</template>

<script>
import axios from 'axios';
import ReportFilter from './ReportFilter.vue';
import CloHeading from '@/base/CloHeading/CloHeading.vue';
import CloStat from './CloStat.vue';
// import CloStat from "@/components/CloStat.vue";
import CloIcon from '@/icons/CloIcon/CloIcon.vue';
import CloDialog from '@/base/CloDialog/CloDialog.vue';

export default {
	name: 'ReportStats',
	components: {
		ReportFilter,
		CloHeading,
		CloStat,
		CloIcon,
		CloDialog,
	},
	data() {
		return {
			numSpeciesStat: '',
			numAddtlTaxaStat: '',
			numChecklistsStat: '',
			mediaStats: {},
			lifers: {},
			numMediaPhotos: '0',
			numMediaAudios: '0',
			tripApiBaseUrl: window.tripApiBaseUrl,
			dialogOpen: false,
			dialogOpener: null,
		};
	},
	props: {
		currentView: {
			type: String,
			default: 'species',
		},
		tripReportId: {
			type: Number,
			required: true,
		},
		selectedPersonId: {
			type: String,
			default: '',
		},
		confirmedPeople: {
			type: Array,
			required: true,
		},
		liferFilters: {
			type: Array,
			default() {
				return [];
			},
		},
		isPrintLayout: Boolean,
	},
	mounted() {
		this.fetchData();
	},
	created() {
		if (this.selectedPersonId || this.confirmedPeople.length == 1) {
			this.getLifers();
		}
	},
	methods: {
		fetchData() {
			this.getNumSpeciesStat();
			this.getNumChecklistsStat();
			this.getMediaStats();
		},
		getNumSpeciesStat() {
			let url = this.tripApiBaseUrl + 'num-species/' + this.tripReportId;
			if (this.selectedPersonId) {
				url = url + '?tripReportPersonId=' + this.selectedPersonId;
			}
			axios
				.get(url)
				.then((res) => {
					this.setNumSpeciesStats(res);
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		getNumChecklistsStat() {
			let url = this.tripApiBaseUrl + 'num-checklists/' + this.tripReportId;
			if (this.selectedPersonId) {
				url = url + '?tripReportPersonId=' + this.selectedPersonId;
			}
			axios
				.get(url)
				.then((res) => {
					this.setNumChecklistsStat(res);
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		getMediaStats() {
			let url = this.tripApiBaseUrl + 'media-stats/' + this.tripReportId;
			if (this.selectedPersonId) {
				url = url + '?tripReportPersonId=' + this.selectedPersonId;
			}
			axios
				.get(url)
				.then((res) => {
					this.setMediaStats(res);
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		getLifers() {
			let personId = '';
			if (this.selectedPersonId) {
				personId = this.selectedPersonId;
			} else if (this.confirmedPeople.length == 1) {
				personId = this.confirmedPeople[0].tripReportPersonId;
			}
			let url = this.tripApiBaseUrl + 'lifer-stats/' + this.tripReportId + '/' + personId;
			axios
				.get(url)
				.then((res) => {
					if (res.data) {
						this.lifers = res.data;
						if (!this.lifers.P) {
							this.lifers.P = 0;
						}
						if (!this.lifers.A) {
							this.lifers.A = 0;
						}
						if (!this.lifers.L) {
							this.lifers.L = 0;
						}
						if (!this.lifers.V) {
							this.lifers.V = 0;
						}
					}
				})
				.catch((err) => {
					console.log('failed', err);
				});
		},
		setNumSpeciesStats(res) {
			this.numSpeciesStat = res.data[0].toString();
			this.numAddtlTaxaStat = res.data[1].toString();
		},
		setNumChecklistsStat(res) {
			this.numChecklistsStat = res.data.toString();
		},
		setMediaStats(res) {
			this.mediaStats = res.data;
			if (this.mediaStats) {
				this.numMediaPhotos = this.mediaStats['P'] ? this.mediaStats['P'].toString() : '0';
				this.numMediaAudios = this.mediaStats['A'] ? this.mediaStats['A'].toString() : '0';
			}
		},
		showDialog(e) {
			this.dialogOpen = true;
			this.dialogOpener = e.target;
		},
		closeDialog() {
			this.dialogOpen = false;
			// important: put focus back on element that triggered the dialog
			if (this.dialogOpener) {
				this.dialogOpener.focus();
			}
		},
	},
	computed: {
		numStats() {
			// Number of stats at the top, used for spacing out grid
			let num = 2; // minimum
			if (this.numMediaPhotos > 0) {
				num = num + 1;
			}
			if (this.numMediaAudios > 0) {
				num = num + 1;
			}
			return num;
		},
		showLifers() {
			let liferTotal = this.lifers.L + this.lifers.A + this.lifers.P + this.lifers.V;
			if (liferTotal > 0 && (this.selectedPersonId || this.confirmedPeople.length == 1)) {
				return true;
			} else {
				return false;
			}
		},
	},
};
</script>

<style lang="scss">
.ReportStats {
	@include inset-responsive;
	padding-bottom: $space-md !important;
}
.StatsTabs {
	list-style: none;
	margin: 0;
	[dir='rtl'] & {
		margin: 0;
	}
	max-width: 100%;
	overflow-x: auto;
	display: grid;
	gap: $space-sm;
	// grid-template-columns: repeat( var(--statsTabColumns, 2), 1fr );
	grid-template-columns: repeat(auto-fit, minmax(6rem, 1fr));
	@include breakpoint(xlarge) {
		grid-template-columns: repeat(auto-fill, minmax(12rem, 1fr));
	}
	li {
		margin: 0;
	}
	.StatsIcon {
		display: flex;
		height: 100%;
	}
	.StatsIcon.StatsIcon--tab {
		@include inset-sm;
		@include breakpoint(large) {
			@include inset-squish-md;
		}
	}
}
.Report-lifers {
	@include text(3);
	display: inline-flex;
	align-items: center;
	gap: $space-sm;
	.Report-lifers-heading {
		color: $color-text-muted;
		margin: 0;
		text-transform: uppercase;
		font-weight: normal;
	}
	ul {
		display: inline-flex;
		align-items: center;
		gap: $space-sm;
	}
	.Report-lifers-item {
		display: inline-flex;
		align-items: center;
		gap: $space-xs;
		@include inset-squish-sm;
		border-radius: $space-md;
		border: 1px solid transparent;
		@include interactiveColor(color, $color-primary-3, darken($color-primary-3, 5%));
		@include interactiveColor(background-color, $color-primary-0, darken($color-primary-0, 5%));
		@include interactiveColor(border-color, transparent, transparentize($color-primary-3, 0.75));
		&[aria-pressed='true'] {
			@include interactiveColor(color, darken($color-secondary-3, 5%), darken($color-secondary-3, 5%));
			@include interactiveColor(background-color, $color-secondary-0, darken($color-secondary-0, 5%));
			@include interactiveColor(border-color, transparentize($color-secondary-3, 0.75), transparentize($color-secondary-3, 0.75));
		}
	}
	.Report-lifers-dialog {
		@include text(4, loose);
		li .Icon {
			color: $color-primary-3;
			transform: translateY(.15rem);
		}
		ul {
			flex-direction: column;
			align-items: stretch;
			gap: $space-xs;
		}
		li {
			display: flex;
			gap: $space-sm;
			align-items: baseline;
		}
	}
}

.print {
	.ReportStats {
		@include inset-md;
		@include stack-md;
		border: 1pt solid $color-neutral-1;
		border-radius: $radius-sm;
	}
	.StatsTabs {
		display: flex;
		gap: $space-lg;
		.StatsIcon.StatsIcon--tab {
			padding: 0;
			border-style: none;
			@include interactiveColor(color, $color-black, $color-black);
			@include interactiveColor(background-color, transparent, transparent);
		}
	}
	.Report-lifers {
		gap: $space-md;
		ul {
			gap: $space-md;
		}
		.Report-lifers-item {
			padding: 0;
			border-style: none;
			@include interactiveColor(color, $color-black, $color-black);
			@include interactiveColor(background-color, transparent, transparent);
		}
	}
}
</style>
